import { type ReactNode, type RefObject, useEffect, useState } from "react";
import { errorMessage, markErrorHandled } from "../../lib/errors";
import { Modal } from "../Modal";
import styles from "./ConfirmDialog.module.css";

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  message: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  danger?: boolean;
  /**
   * May return a promise; the button shows a spinner and the dialog closes when it resolves. A rejection keeps the
   * dialog open and shows the error inside it, so the user can retry or cancel.
   */
  onConfirm: () => void | Promise<void>;
  /** See Modal: the element that gets focus back on close, when the opener does not survive (a menu item). */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

export function ConfirmDialog({
  open,
  onClose,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger,
  onConfirm,
  returnFocusRef,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) setError(null);
  }, [open]);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      markErrorHandled(err);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={title}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      confirmVariant={danger ? "danger" : "primary"}
      confirmLoading={busy}
      onConfirm={() => void confirm()}
      returnFocusRef={returnFocusRef}
    >
      <div className={styles.message}>{message}</div>
      {error !== null && (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      )}
    </Modal>
  );
}
